'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, ImageField, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useIsClient } from '@/hooks';

/**
 * Props for `SideBySideBenefitList`.
 */
export type SideBySideBenefitListProps =
  SliceComponentProps<Content.SideBySideBenefitListSlice>;

/**
 * Component for "SideBySideBenefitList" Slices.
 * Two-column layout with intro text on left and benefit list card on right.
 */
const SideBySideBenefitList: FC<SideBySideBenefitListProps> = ({ slice }) => {
  const { intro_title, intro_description, right_headline, benefits } =
    slice.primary;

  // Extract background from slice.primary if available
  const background =
    'background' in slice.primary
      ? (slice.primary.background as ImageField)
      : null;

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: leftRef, delay: '0ms' },
      { ref: rightRef, delay: '200ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [isClient]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative py-[80px] px-4 md:px-8 lg:px-[150px]"
      style={{ backgroundColor: '#0e1010' }}
    >
      {/* Background Image Layer */}
      {isFilled.image(background) && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <PrismicNextImage
            field={background}
            className="absolute w-full h-[226%] left-0 top-[-12%] object-cover max-w-none opacity-50"
            fill={false}
            priority
          />
        </div>
      )}

      <div className="relative max-w-[1140px] mx-auto flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center">
        {/* Left Side - Intro Content */}
        <div
          ref={leftRef}
          className="w-full lg:w-[520px] shrink-0 flex flex-col gap-6 items-start opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Title */}
          {isFilled.richText(intro_title) && (
            <PrismicRichText
              field={intro_title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white w-full">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {/* Description */}
          {isFilled.richText(intro_description) && (
            <div className="w-full text-lg md:text-xl font-normal leading-[1.5] text-white space-y-4">
              <PrismicRichText
                field={intro_description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="mb-0">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                  hyperlink: ({ children, node }) => (
                    <a
                      href={node.data.url}
                      className="text-[#05f273] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Right Side - Benefits Card */}
        <div
          ref={rightRef}
          className="flex-1 w-full backdrop-blur-[10px] bg-[rgba(31,34,40,0.8)] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] flex flex-col gap-4 overflow-hidden opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Card Headline */}
          {right_headline && (
            <h3 className="text-xl md:text-2xl font-medium leading-[1.2] text-white w-full">
              {right_headline}
            </h3>
          )}

          {/* Benefits List */}
          {benefits && benefits.length > 0 && (
            <div className="flex flex-col gap-4 w-full">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-6 items-center w-full">
                  {/* Icon */}
                  {isFilled.image(benefit.icon) && (
                    <div className="w-12 h-12 shrink-0">
                      <PrismicNextImage
                        field={benefit.icon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}

                  {/* Text */}
                  {benefit.text && (
                    <p className="flex-1 text-lg md:text-xl font-normal leading-[1.4] text-white">
                      {benefit.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SideBySideBenefitList;
